import setContextualFormatBarTarget from '../actions/setContextualFormatBarTarget';
import {
    lazyFormatPainterFinish,
    lazyShowInsertLinkDialog,
    ContextualFormatBar,
    InsertLinkSource,
} from '../index';
import updateRibbonState from '../actions/updateRibbonState';
import type RibbonViewState from '../store/schema/RibbonViewState';
import type { EditorViewState, PluginViewStateAdapter, PluginWithUI } from 'owa-editor';
import { isMac } from 'owa-user-agent/lib/userAgent';
import { ContentEditFeature, IEditor, PluginEvent, PluginEventType } from 'roosterjs-editor-types';
import { owaDate } from 'owa-datetime';

const KEY_ESC = 27;
const KEY_K = 75;

const hasCtrlOrMetaKey: (event: KeyboardEvent) => boolean = isMac()
    ? e => e.metaKey
    : e => e.ctrlKey;

export default class RibbonPlugin implements PluginWithUI<RibbonViewState> {
    private editor: IEditor;

    constructor(
        public readonly adapter: PluginViewStateAdapter<RibbonViewState>,
        public readonly editorAdapter: PluginViewStateAdapter<EditorViewState>,
        public allowCtrlK: boolean = true
    ) {}

    public getName() {
        return 'Ribbon';
    }

    public initialize(editor: IEditor): void {
        this.editor = editor;
        if (this.allowCtrlK) {
            this.editor.addContentEditFeature(this.insertLinkFeature);
        }
        this.editor.addContentEditFeature(this.closeContextualFormatBarFeature);
    }

    public onPluginEvent(event: PluginEvent): void {
        const ribbonViewState = this.adapter();

        if (
            (event.eventType == PluginEventType.KeyUp && event.rawEvent.keyCode != KEY_ESC) ||
            event.eventType == PluginEventType.MouseUp ||
            event.eventType == PluginEventType.ContentChanged
        ) {
            let updateContextFormatBar = event.eventType != PluginEventType.ContentChanged;
            updateRibbonState(
                this.editorAdapter(),
                ribbonViewState,
                updateContextFormatBar,
                true /*delay*/
            );
        }

        if (
            event.eventType == PluginEventType.MouseUp &&
            ribbonViewState.formatViewState.formatPainterFormat
        ) {
            // NOTE: When the editor is disposed, we trigger the plugin event "BeforeDispose",
            // so check the event type first (VSO 54192)
            lazyFormatPainterFinish
                .import()
                .then(formatPainterFinish =>
                    formatPainterFinish(this.editorAdapter(), this.adapter().formatViewState)
                );
        }
    }

    public dispose(): void {
        this.editor = null;
    }

    public getUIComponentClass() {
        return ContextualFormatBar;
    }

    public isDarkMode() {
        return this.editor?.isDarkMode();
    }

    private insertLinkFeature: ContentEditFeature = {
        keys: [KEY_K],
        allowFunctionKeys: true,
        shouldHandleEvent: event => {
            const rawEvent = event.rawEvent;
            return !rawEvent.altKey && !rawEvent.shiftKey && hasCtrlOrMetaKey(rawEvent);
        },
        handleEvent: event => {
            const rawEvent = event.rawEvent;
            rawEvent.preventDefault();
            rawEvent.stopPropagation();
            lazyShowInsertLinkDialog
                .import()
                .then(showInsertLinkDialog =>
                    showInsertLinkDialog(
                        this.editorAdapter(),
                        this.editor.getDocument().defaultView,
                        InsertLinkSource.ShortKey,
                        owaDate()
                    )
                );
        },
    };

    private closeContextualFormatBarFeature: ContentEditFeature = {
        keys: [KEY_ESC],
        shouldHandleEvent: () => !!this.adapter().targetPoint,
        handleEvent: event => {
            const rawEvent = event.rawEvent;
            rawEvent.preventDefault();
            rawEvent.stopPropagation();
            setContextualFormatBarTarget(this.adapter(), null /*targetPoint*/);
        },
    };
}
